import React, { useState, useEffect, useLayoutEffect, useRef, ReactNode, forwardRef, useMemo, useImperativeHandle, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { TextInput } from './text-input';
import { CalendarIcon } from '../agent-dashboard/assets';
import { TextButton } from './text-button';
import { cn } from '@/lib/utils';
import { IconButton } from './icon-button';
import { LeftIcon, RightIcon } from '../ui/icon';
import { formatDateToYYYYMMDD } from '@/utils/date-formatter';
import { useTranslation } from 'react-i18next';

/**
 * 日期网格项接口
 */
interface DateGridItem {
  date: Date;
  currentMonth: boolean;
}

export interface DatePickerActions {
  reset: () => void;
}

/**
 * 日期选择器属性接口
 */
interface DatePickerProps {
  /**
   * 是否为范围选择器
   * @default false
   */
  range?: boolean;
  /**
   * 单个日期选择模式下的默认值
   */
  defaultValue?: Date;
  /**
   * 范围选择模式下的默认值 [开始日期, 结束日期]
   */
  defaultRange?: [Date | null, Date | null];
  /**
   * 选择日期后的回调函数
   * 单个日期模式返回选中的日期，范围模式返回日期范围数组
   */
  onConfirm?: (date: Date | [Date, Date]) => void;
  /**
   * 输入框占位符
   * 范围模式下为数组 [开始日期占位符, 结束日期占位符]
   */
  placeholder?: string | [string, string];
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 开始日期触发器，用于自定义开始日期的输入元素
   */
  startTrigger?: ReactNode;
  /**
   * 结束日期触发器，用于自定义结束日期的输入元素（仅在范围模式下有效）
   */
  endTrigger?: ReactNode;
}

/**
 * 日期选择器组件
 * 支持单日期选择和日期范围选择两种模式
 */
export const DatePicker = forwardRef<DatePickerActions, DatePickerProps>(({
  range = false,
  defaultValue,
  defaultRange,
  onConfirm,
  placeholder = range ? ['开始日期', '结束日期'] : '选择日期',
  className = '',
  startTrigger,
  endTrigger
}, ref) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultValue || null
  );
  const [rangeDates, setRangeDates] = useState<[Date | null, Date | null]>(
    defaultRange || [null, null]
  );
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const pickerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 8 });
  const [popoverPlacement, setPopoverPlacement] = useState<'top' | 'bottom'>('bottom');

  const VIEWPORT_PADDING = 12;
  const POPOVER_GAP = 8;

  const updatePopoverPosition = useCallback(() => {
    const container = pickerRef.current;
    const popover = popoverRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const popoverWidth = popover?.offsetWidth ?? (range ? 656 : 328);
    const popoverHeight = popover?.offsetHeight ?? 468;

    let left = containerRect.left;

    if (left + popoverWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = containerRect.right - popoverWidth;
    }
    if (left + popoverWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - VIEWPORT_PADDING - popoverWidth;
    }
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    let top = containerRect.bottom + POPOVER_GAP;
    const fitsBelow =
      top + popoverHeight <= window.innerHeight - VIEWPORT_PADDING;

    if (!fitsBelow) {
      const topAbove = containerRect.top - popoverHeight - POPOVER_GAP;
      if (topAbove >= VIEWPORT_PADDING) {
        top = topAbove;
      }
    }

    setPopoverPosition({ left, top });
    setPopoverPlacement(top < containerRect.top ? 'top' : 'bottom');
  }, [range]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePopoverPosition();
  }, [isOpen, range, updatePopoverPosition]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, updatePopoverPosition]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setSelectedDate(null);
      setRangeDates([null, null]);
    }
  }));

  // 点击外部关闭日期选择器
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        pickerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 当defaultValue变化时更新选中日期
  useEffect(() => {
    if (defaultValue) {
      setSelectedDate(defaultValue);
    }
  }, [defaultValue]);

  // 当defaultRange变化时更新范围日期
  useEffect(() => {
    if (defaultRange) {
      setRangeDates(defaultRange);
    }
  }, [defaultRange]);

  /**
   * 生成指定月份的日期网格
   * @param date 包含月份的日期对象
   * @returns 日期网格数据
   */
  const generateDaysGrid = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // 获取当月第一天是星期几 (0-6, 0是星期日)
    const firstDay = new Date(year, month, 1).getDay() || 7; // 转换为1-7，1是星期一
    // 获取当月的天数
    const daysCount = new Date(year, month + 1, 0).getDate();

    // 获取上个月的最后几天
    const prevMonth = new Date(year, month, 0);
    const prevMonthYear = prevMonth.getFullYear();
    const prevMonthNum = prevMonth.getMonth();
    const prevMonthDays = prevMonth.getDate();

    const days: DateGridItem[] = [];

    // 添加上个月的尾部日期
    for (let i = 0; i < firstDay - 1; i++) {
      const day = prevMonthDays - (firstDay - 2 - i);
      days.push({
        date: new Date(prevMonthYear, prevMonthNum, day),
        currentMonth: false
      });
    }

    // 添加当月日期
    for (let i = 1; i <= daysCount; i++) {
      days.push({
        date: new Date(year, month, i),
        currentMonth: true
      });
    }

    // 计算需要补充的下个月日期数量
    const nextMonthDaysCount = 42 - days.length; // 6行7列共42个格子

    // 添加下个月的头部日期
    for (let i = 1; i <= nextMonthDaysCount; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        currentMonth: false
      });
    }

    return days;
  };

  /**
   * 检查日期是否为今天
   * @param date 要检查的日期
   * @returns 是否为今天
   */
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  /**
   * 检查日期是否被选中
   * @param date 要检查的日期
   * @returns 是否被选中
   */
  const isSelected = (date: Date) => {
    if (!range) {
      return selectedDate &&
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
    } else {
      return (rangeDates[0] &&
        date.getDate() === rangeDates[0]!.getDate() &&
        date.getMonth() === rangeDates[0]!.getMonth() &&
        date.getFullYear() === rangeDates[0]!.getFullYear()) ||
        (rangeDates[1] &&
          date.getDate() === rangeDates[1]!.getDate() &&
          date.getMonth() === rangeDates[1]!.getMonth() &&
          date.getFullYear() === rangeDates[1]!.getFullYear());
    }
  };

  /**
   * 检查日期是否在选中的范围内
   * @param date 要检查的日期
   * @returns 是否在范围内
   */
  const isInRange = (date: Date) => {
    if (!range || !rangeDates[0] || !rangeDates[1]) return false;

    const start = new Date(Math.min(rangeDates[0].getTime(), rangeDates[1].getTime()));
    const end = new Date(Math.max(rangeDates[0].getTime(), rangeDates[1].getTime()));

    return date >= start && date <= end;
  };

  /**
   * 检查日期是否是范围的开始
   * @param date 要检查的日期
   * @returns 是否是范围开始
   */
  const isRangeStart = (date: Date) => {
    if (!range || !rangeDates[0]) return false;
    return date.toDateString() === rangeDates[0]!.toDateString();
  };

  /**
   * 检查日期是否是范围的结束
   * @param date 要检查的日期
   * @returns 是否是范围结束
   */
  const isRangeEnd = (date: Date) => {
    if (!range || !rangeDates[1]) return false;
    return date.toDateString() === rangeDates[1]!.toDateString();
  };

  /**
   * 处理日期点击事件
   * @param date 被点击的日期
   */
  const handleDateClick = (date: Date) => {
    if (!range) {
      setSelectedDate(date);
    } else {
      // 范围选择逻辑
      const [start, end] = rangeDates;

      // 如果还没有选择开始日期，设置为开始日期
      if (!start) {
        setRangeDates([date, null]);
      }
      // 如果已经选择了开始日期但没有结束日期
      else if (start && !end) {
        // 如果选择的日期在开始日期之后，设置为结束日期
        if (date >= start) {
          setRangeDates([start, date]);
        }
        // 否则，交换开始和结束日期
        else {
          setRangeDates([date, start]);
        }
      }
      // 如果已经选择了完整范围，重新开始选择
      else {
        setRangeDates([date, null]);
      }
    }
  };

  /**
   * 处理鼠标悬停事件（用于范围选择预览）
   * @param date 悬停的日期
   */
  const handleDateHover = (date: Date) => {
    if (range && rangeDates[0] && !rangeDates[1]) {
      setHoveredDate(date);
    }
  };

  /**
   * 处理确认按钮点击事件
   */
  const handleConfirm = () => {
    if (!range && selectedDate) {
      onConfirm?.(selectedDate);
    } else if (range && rangeDates[0] && rangeDates[1]) {
      onConfirm?.(rangeDates);
    }
    setIsOpen(false);
  };

  /**
   * 切换到上一个月
   */
  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  /**
   * 切换到下一个月
   */
  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  /**
   * 获取下个月的日期对象
   * @returns 下个月的日期
   */
  const getNextMonth = () => {
    return new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
  };

  /**
   * 格式化月份为"Month YYYY"格式
   * @param date 日期对象
   * @returns 格式化后的月份字符串
   */
  const formatMonth = (date: Date) => {
    return date.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  };

  // 生成当前月和下个月（如果是范围选择）的日期网格
  const currentMonthDays = generateDaysGrid(currentMonth);
  const nextMonthDays = range ? generateDaysGrid(getNextMonth()) : [];

  // 渲染输入框显示的文本
  const startDisplayText = useMemo(() => {
    let text = '';
    if (range) {
      text = rangeDates[0] ? formatDateToYYYYMMDD(rangeDates[0]) : '';
    } else {
      text = selectedDate ? formatDateToYYYYMMDD(selectedDate) : '';
    }
    return text;
  }, [range, rangeDates, selectedDate]);

  const endDisplayText = useMemo(() => {
    let text = '';
    if (range && rangeDates[1]) {
      text = formatDateToYYYYMMDD(rangeDates[1]);
    }
    return text;
  }, [range, rangeDates]);

  // 渲染日期单元格的通用函数
  const renderDateCell = ({ date, currentMonth }: DateGridItem) => {
    const isDateSelected = isSelected(date);
    const inRange = isInRange(date);
    const isStart = isRangeStart(date);
    const isEnd = isRangeEnd(date);
    const today = isToday(date);
    const isHoverInRange = range && rangeDates[0] && !rangeDates[1] &&
      hoveredDate && date > rangeDates[0] && date < hoveredDate;

    // 获取星期几 (0-6, 0是星期日)
    const dayOfWeek = date.getDay();
    const isMonday = dayOfWeek === 1;
    const isSunday = dayOfWeek === 0;

    // 计算日期单元格的样式
    let bgClass = '';
    let roundedClass = ''; // 默认不使用圆角

    // 选中日期样式 - 使用指定的紫色
    if (isDateSelected) {
      // 选中的日期
      bgClass = 'bg-[rgba(117,66,247,1)] text-white';
      roundedClass = 'rounded-full'; // 选中的单个日期使用圆形
    } else if (today) {
      // 是当日
      bgClass = 'bg-[rgba(242,244,247,1)] text-gray-800';
      roundedClass = 'rounded-full'; // 今天使用圆形
    } else if (!currentMonth) {
      // 不是当月的日期
      bgClass = 'text-gray-300 hover:bg-gray-100';
    } else {
      // 当月的日期
      bgClass = 'font-medium text-[rgba(52,64,84,1)] hover:bg-[rgba(242,244,247,1)]';
    }

    // 范围选择的背景样式 - 使用指定的背景色
    let rangeBgClass = '';
    if (range && (inRange || isHoverInRange) && !isDateSelected) {
      rangeBgClass = 'bg-[rgba(249,250,251,1)]';

      // 根据周一和周日设置相应的圆角
      if (isMonday) {
        roundedClass = 'rounded-tl-full rounded-bl-full'; // 周一设置左上和左下圆角
      } else if (isSunday) {
        roundedClass = 'rounded-tr-full rounded-br-full'; // 周日设置右上和右下圆角
      } else {
        roundedClass = ''; // 其他日期不设置圆角
      }
    }

    return (
      <div
        key={date.toISOString()}
        className={cn(
          "flex items-center justify-center w-full size-10 mb-0.5 text-sm transition-all duration-200 cursor-pointer",
          bgClass, rangeBgClass, roundedClass,
          !inRange && "hover:rounded-full",
          (inRange && !isStart && !isEnd) && "hover:bg-[rgba(249,250,251,1)]",
        )}
        onClick={() => handleDateClick(date)}
        onMouseEnter={() => handleDateHover(date)}
      >
        {date.getDate()}
      </div>
    );
  };

  // 渲染触发器
  const renderTrigger = () => {
    // 单日期模式
    if (!range) {
      if (startTrigger) {
        return React.cloneElement(startTrigger as React.ReactElement, {
          onClick: () => setIsOpen(!isOpen),
        });
      }

      return (
        <TextInput
          prefixIcon={<CalendarIcon />}
          ref={inputRef}
          type="text"
          readOnly
          value={startDisplayText || (typeof placeholder === 'string' ? placeholder : placeholder[0])}
          className="h-9"
          placeholder={typeof placeholder === 'string' ? placeholder : placeholder[0]}
          onClick={() => setIsOpen(!isOpen)}
        />
      );
    }

    // 范围选择模式
    return (
      <div className="flex items-center space-x-3">
        {startTrigger ? (
          React.cloneElement(startTrigger as React.ReactElement, {
            onClick: () => setIsOpen(!isOpen),
          })
        ) : (
          <TextInput
            prefixIcon={<CalendarIcon />}
            type="text"
            readOnly
            value={startDisplayText || (Array.isArray(placeholder) ? placeholder[0] : '开始日期')}
            className="h-9"
            placeholder={Array.isArray(placeholder) ? placeholder[0] : '开始日期'}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}

        <span className="text-gray-400">-</span>

        {endTrigger ? (
          React.cloneElement(endTrigger as React.ReactElement, {
            onClick: () => setIsOpen(!isOpen),
          })
        ) : (
          <TextInput
            prefixIcon={<CalendarIcon />}
            type="text"
            readOnly
            value={endDisplayText || (Array.isArray(placeholder) ? placeholder[1] : '结束日期')}
            className="h-9"
            placeholder={Array.isArray(placeholder) ? placeholder[1] : '结束日期'}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      {renderTrigger()}

      {/* 日期选择器弹窗 */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={popoverRef}
              key="date-picker-popover"
              initial={{
                opacity: 0,
                scale: 0.96,
                y: popoverPlacement === 'bottom' ? -8 : 8,
              }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{
                opacity: 0,
                scale: 0.96,
                y: popoverPlacement === 'bottom' ? -8 : 8,
              }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'fixed',
                left: popoverPosition.left,
                top: popoverPosition.top,
              }}
              className={cn(
                "z-50 w-[41rem] h-[29.25rem] bg-white rounded-lg shadow-xl overflow-hidden border border-gray-100 select-none",
                popoverPlacement === 'bottom' ? 'origin-top' : 'origin-bottom',
                !range && 'w-[20.5rem]',
              )}
            >
          {/* 日历网格 */}
          <div className={`grid ${range ? 'grid-cols-2' : 'grid-cols-1'} gap-0 h-[24.5rem] `}>
            {/* 当月日历 */}
            <div className="py-5 px-6 h-full">
              <div className="flex items-center justify-between mt-2 mb-6">
                <IconButton icon={<LeftIcon />} onClick={prevMonth} />
                <span className="text-base font-semibold text-[rgba(52,64,84,1)]">{formatMonth(currentMonth)}</span>
                <IconButton icon={<RightIcon />} onClick={nextMonth} />
              </div>

              {/* 星期标题 */}
              <div className="grid grid-cols-7 mb-1">
                {['一', '二', '三', '四', '五', '六', '日'].map((day, index) => (
                  <div key={index} className="flex items-center justify-center text-xs text-gray-500 size-10">
                    {day}
                  </div>
                ))}
              </div>

              {/* 日期网格 */}
              <div className="grid grid-cols-7 gap-0">
                {currentMonthDays.map(renderDateCell)}
              </div>
            </div>

            {/* 下个月日历（仅在范围选择模式下显示） */}
            {range && (
              <div className="py-5 px-6 border-l border-gray-100 h-full">
                <div className="flex items-center justify-between mt-2 mb-6">
                  <IconButton icon={<LeftIcon />} onClick={prevMonth} />
                  <span className="text-base font-semibold text-[rgba(52,64,84,1)]">{formatMonth(getNextMonth())}</span>
                  <IconButton icon={<RightIcon />} onClick={nextMonth} />
                </div>

                {/* 星期标题 */}
                <div className="grid grid-cols-7 mb-1">
                  {['一', '二', '三', '四', '五', '六', '日'].map((day, index) => (
                    <div key={index} className="flex items-center justify-center  text-xs text-gray-500 py-1 size-10">
                      {day}
                    </div>
                  ))}
                </div>

                {/* 日期网格 */}
                <div className="grid grid-cols-7 gap-0">
                  {nextMonthDays.map(renderDateCell)}
                </div>
              </div>
            )}
          </div>

          {/* 底部操作栏 */}
          <div className="flex items-center justify-between p-4 border-t border-gray-100 h-[4.75rem]">
            <div className="flex text-base text-[rgba(16,24,40,1)] items-center">
              {!range
                ? startDisplayText || 'Select date'
                : (
                  <div className='flex items-center justify-center gap-3 '>
                    <div className='w-[8.5rem] h-11 flex items-center justify-center border border-[rgba(208,213,221,1)] rounded-sm'>
                      {startDisplayText || t('date_picker.select_date')}
                    </div>
                    <span>-</span>
                    <div className='w-[8.5rem] h-11 flex items-center justify-center border border-[rgba(208,213,221,1)] rounded-sm'>
                      {endDisplayText || t('date_picker.select_date')}
                    </div>
                  </div>
                )}
            </div>
            <div className="flex space-x-3">
              <TextButton
                label={t('date_picker.cancel')}
                onClick={() => setIsOpen(false)}
                className={cn(
                  '!w-20 h-10 bg-white',
                  'font-semibold text-sm !text-[rgba(52,64,84,1)]',
                  'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
                )}
              />
              <TextButton
                label={t('date_picker.confirm')}
                onClick={handleConfirm}
                disabled={(!range && !selectedDate) || (range && (!rangeDates[0] || !rangeDates[1]))}
                className='!w-[5.5rem] text-sm font-semibold'
              />
            </div>
          </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
});

DatePicker.displayName = 'DatePicker';
